import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, DatePicker, Row, Select, Space, Statistic, Table } from 'antd';
import moment, { Moment } from 'moment';
import PageHeader from '../../components/business/PageHeader';
import RequestState from '../../components/business/RequestState';
import { V2ApiError, v2Api } from '../../services/v2';
import { asV2Error, formatDate, formatDateTime, formatMoney } from '../../utils/v2';

const topics: { [key: string]: string } = {
    governance: '客户与治理',
    acquisition: '获客转化',
    email: '邮件执行',
    opportunity: '商机结果',
    project: '项目交付',
    payment: '开票回款',
    settlement: '合作结算',
};
const AnalyticsPage = () => {
    const [topic, setTopic] = useState('opportunity');
    const [range, setRange] = useState<[Moment, Moment]>([moment().subtract(30, 'days'), moment()]);
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<V2ApiError | null>(null);
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setItems(
                await v2Api.analytics(topic, {
                    from: range[0].format('YYYY-MM-DD'),
                    to: range[1].format('YYYY-MM-DD'),
                })
            );
        } catch (e) {
            setError(asV2Error(e));
        } finally {
            setLoading(false);
        }
    }, [topic, range]);
    useEffect(() => {
        load();
    }, [load]);
    const latest = useMemo(() => {
        const map: any = {};
        items.forEach((item) => {
            map[`${item.metricCode}-${item.currency || ''}`] = item;
        });
        return Object.keys(map)
            .map((key) => map[key])
            .slice(0, 6);
    }, [items]);
    const columns = [
        { title: '日期', dataIndex: 'metricDate', width: 110, render: formatDate },
        { title: '指标', dataIndex: 'metricCode', width: 210 },
        {
            title: '维度',
            key: 'dimension',
            width: 180,
            render: (_: any, row: any) =>
                `${row.dimensionType || 'ALL'} · ${row.dimensionKey || '全部'}`,
        },
        {
            title: '值',
            key: 'value',
            align: 'right' as const,
            render: (_: any, row: any) =>
                row.calculable === false ? (
                    <span className="v2-muted">不可计算</span>
                ) : row.currency ? (
                    formatMoney(row.value, row.currency)
                ) : (
                    row.value
                ),
        },
        {
            title: '分子 / 分母',
            key: 'ratio',
            width: 150,
            render: (_: any, row: any) =>
                row.denominator == null ? '—' : `${row.numerator || 0} / ${row.denominator}`,
        },
        { title: '缺失值', dataIndex: 'missingValueCount', width: 90 },
        { title: '最近重建', dataIndex: 'rebuiltAt', width: 170, render: formatDateTime },
    ];
    return (
        <div className="v2-page">
            <PageHeader
                eyebrow="经营分析 · 权限化指标"
                title="经营分析"
                description="回答经营结果如何；所有指标沿用服务端口径，分母为零显示不可计算，跨币种缺失单独提示。"
            />
            <Card className="v2-filter-card">
                <Space className="v2-wrap-space">
                    <Select
                        value={topic}
                        style={{ width: 180 }}
                        onChange={(value) => setTopic(String(value))}
                    >
                        {Object.keys(topics).map((key) => (
                            <Select.Option key={key} value={key}>
                                {topics[key]}
                            </Select.Option>
                        ))}
                    </Select>
                    <DatePicker.RangePicker
                        value={range}
                        allowClear={false}
                        onChange={(values) => values && setRange(values as [Moment, Moment])}
                    />
                </Space>
            </Card>
            <RequestState loading={loading} error={error} empty={!items.length} onRetry={load}>
                <Row gutter={[12, 12]} className="v2-stat-grid">
                    {latest.map((item) => (
                        <Col
                            xs={24}
                            sm={12}
                            lg={8}
                            xl={4}
                            key={`${item.metricCode}-${item.currency || ''}`}
                        >
                            <Card className="v2-stat-card">
                                <Statistic
                                    title={item.metricCode}
                                    value={
                                        item.calculable === false
                                            ? '不可计算'
                                            : Number(item.value || 0)
                                    }
                                    suffix={item.currency}
                                />
                                <div className="v2-muted">{formatDate(item.metricDate)}</div>
                            </Card>
                        </Col>
                    ))}
                </Row>
                {items.some((item) => Number(item.missingValueCount) > 0) && (
                    <Alert
                        className="v2-section"
                        type="warning"
                        showIcon
                        message="部分记录缺少汇率或统计值"
                        description="缺失数据未被静默计入汇总，请结合列表中的缺失值数量判断口径完整性。"
                    />
                )}
                <Card className="v2-panel">
                    <Table
                        rowKey={(row: any, index: number) =>
                            `${row.metricDate}-${row.metricCode}-${row.dimensionType}-${row.dimensionKey}-${index}`
                        }
                        columns={columns}
                        dataSource={items}
                        scroll={{ x: 980 }}
                        pagination={{ pageSize: 20 }}
                    />
                </Card>
            </RequestState>
        </div>
    );
};
export default AnalyticsPage;
